'use client';
import { cn } from '@/lib/utils';
import { cva, cx } from 'cva';
import { Loader2 } from 'lucide-react';
import React from 'react';
const button = cva('button', {
  variants: {
    variant: {
      filled: ['rounded-[24px]'],
      outlined: ['rounded-[24px]'],
      plain: ['bg-transparent hover:bg-body-background'],
      icon: [
        'bg-transparent hover:bg-body-background disabled:bg-transparent rounded-full',
      ],
      text: ['bg-transparent text-16 font-medium'],
      chip: [
        'px-3 py-2 rounded-2xl text-text-primary text-14 font-dmsans font-medium',
      ],
    },
    size: {
      xs: ['px-4 py-0.5'],
      sm: ['px-4 py-1'],
      md: ['px-4 py-2 lg:px-7'],
    },
    color: {
      primary: [],
      secondary: [],
      transparent: [],
    },
    selected: {
      true: [],
      false: [],
    },
    disabled: {
      true: ['cursor-default'],
      false: ['cursor-pointer'],
    },
  },
  compoundVariants: [
    {
      variant: 'filled',
      disabled: true,
      className: 'border border-body-background text-text-disabled',
    },
    {
      variant: 'filled',
      color: 'primary',
      disabled: false,
      className: 'bg-primary text-white',
    },
    {
      variant: 'filled',
      color: 'secondary',
      disabled: false,
      className: 'bg-secondary text-white',
    },
    {
      variant: 'outlined',
      disabled: true,
      className: 'border border-body-background text-text-disabled bg-white',
    },
    {
      variant: 'outlined',
      color: 'primary',
      disabled: false,
      className:
        'border border-primary text-primary bg-white hover:bg-primary-light-1',
    },
    {
      variant: 'outlined',
      color: 'secondary',
      disabled: false,
      className: 'border border-secondary text-secondary bg-white',
    },
    {
      variant: 'text',
      color: 'primary',
      className: 'text-primary',
    },
    {
      variant: 'text',
      color: 'secondary',
      className: 'text-secondary',
    },
    {
      variant: 'text',
      selected: true,
      color: undefined,
      className: 'text-text-primary',
    },
    {
      variant: 'text',
      selected: false,
      color: undefined,
      className: 'text-text-disabled hover:text-text-primary',
    },
    {
      variant: 'chip',
      color: 'secondary',
      selected: true,
      className: 'border border-transparent bg-chips-secondary',
    },
    {
      variant: 'chip',
      color: 'secondary',
      selected: false,
      className: 'border border-chips-secondary bg-transparent',
    },
    {
      variant: 'chip',
      color: 'primary',
      selected: true,
      className: 'border border-transparent bg-chips-primary',
    },
    {
      variant: 'chip',
      color: 'primary',
      selected: false,
      className: 'border border-chips-primary bg-transparent',
    },
    {
      variant: ['filled', 'outlined'],
      size: 'sm',
      className: 'text-14 font-medium',
    },
    {
      variant: ['filled', 'outlined'],
      size: 'xs',
      className: 'text-11 font-medium',
    },
    {
      variant: 'icon',
      size: 'xs',
      className: '!p-0.5 !sm:p-1',
    },
    {
      variant: 'icon',
      size: 'sm',
      className: '!p-1 !sm:p-1.5',
    },
    {
      variant: 'icon',
      size: 'md',
      className: '!p-1.5 !sm:p-2',
    },
    {
      variant: 'icon',
      size: 'lg',
      className: '!p-2 !sm:p-4',
    },
  ],
  defaultVariants: {
    variant: 'filled',
    size: 'md',
    color: 'primary',
    selected: false,
    disabled: false,
  },
});

const Button = React.forwardRef((props, buttonRef) => {
  const {
    variant,
    color,
    className,
    children,
    selected,
    disabled,
    size,
    as = 'button',
    type = 'button',
    onClick,
    loading = false,
    ...rest
  } = props;
  const ref = React.useRef(null);
  if (!buttonRef) {
    buttonRef = ref;
  }

  const Tag = as;

  const handleClick = (event) => {
    if (disabled || loading) {
      return;
    }

    const btn = buttonRef.current;
    const circle = document.createElement('span');
    let bgClass = 'bg-ripple';
    if (variant !== 'filled') {
      if (color === 'primary') {
        bgClass = 'bg-primary-light-1';
      } else if (color === 'secondary') {
        bgClass = 'bg-secondary-light-1';
      }
    }
    if (btn && circle) {
      const diameter = Math.max(btn.clientWidth, btn.clientHeight);
      const radius = diameter / 2;
      const { left, top } = btn.getBoundingClientRect();
      circle.style.width = circle.style.height = `${diameter}px`;
      circle.style.left = `${event.clientX - (left + radius)}px`;
      circle.style.top = `${event.clientY - (top + radius)}px`;
      circle.style.transform = 'scale(0)';
      circle.classList.add(
        'ripple',
        'absolute',
        'rounded-full',
        'animation-ripple',
        'animate-ripple',
        bgClass,
      );
      const ripple = btn.getElementsByClassName('ripple')[0];
      if (ripple) {
        ripple.remove();
      }
      btn.appendChild(circle);
    }
    if (onClick) {
      onClick(event);
    }
  };
  return (
    <Tag
      ref={buttonRef}
      className={cn(
        button({
          variant: variant,
          color: color,
          selected: selected,
          className: cn(
            'relative overflow-hidden focus-visible:outline-none',
            loading && 'cursor-default opacity-60',
            className,
          ),
          disabled: disabled || loading,
          size: size,
        }),
      )}
      disabled={disabled || loading}
      onClick={handleClick}
      type={type}
      {...rest}>
      <div className={cx(loading && 'opacity-0')}>{children}</div>
      {loading && (
        <div className="absolute inset-0 flex h-full w-full items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}
    </Tag>
  );
});
Button.displayName = 'Button';

export { button as buttonVariants };
export default Button;
